import React from 'react';
import { Occurrence, OccurrenceStatus, OccurrenceSeverity } from '../types';
import { TablePagination, useTablePagination } from '../../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface OccurrencesTableProps {
  occurrences: Occurrence[];
  loading: boolean;
  onSelect: (occurrence: Occurrence) => void;
  onRefresh: () => void;
}

export const OccurrencesTable: React.FC<OccurrencesTableProps> = ({
  occurrences,
  loading,
  onSelect,
}) => {
  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(occurrences);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);
  const getStatusBadge = (status: OccurrenceStatus) => {
    switch (status) {
      case 'NEW':
        return <span className="badge-pill badge-info">New</span>;
      case 'UNDER_REVIEW':
        return <span className="badge-pill badge-warning">Under Review</span>;
      case 'VERIFIED':
        return <span className="badge-pill badge-success">Verified</span>;
      case 'INVESTIGATION_REQUIRED':
        return <span className="badge-pill badge-danger">Investigation Req</span>;
      case 'UNDER_INVESTIGATION':
        return <span className="badge-pill badge-warning">Under Investigation</span>;
      case 'PENDING_ACTIONS':
        return <span className="badge-pill badge-warning">Pending Actions</span>;
      case 'PENDING_CLOSURE':
        return <span className="badge-pill badge-info">Pending Closure</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-neutral">Closed</span>;
      case 'REOPENED':
        return <span className="badge-pill badge-danger">Reopened</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  const getSeverityBadge = (severity: OccurrenceSeverity) => {
    switch (severity) {
      case 'CRITICAL':
        return <span className="badge-pill badge-danger">Critical</span>;
      case 'HIGH':
        return <span className="badge-pill badge-warning">High</span>;
      case 'NORMAL':
        return <span className="badge-pill badge-info">Normal</span>;
      case 'LOW':
        return <span className="badge-pill badge-success">Low</span>;
      default:
        return <span className="badge-pill badge-neutral">{severity}</span>;
    }
  };

  if (!loading && occurrences.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>
        <p style={{ margin: 0, fontSize: '15px' }}>No occurrences found for the selected view.</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="occurrenceNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Number
              </SortableTh>
              <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Title & Narrative
              </SortableTh>
              <SortableTh field="eventDate" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Event Date (UTC)
              </SortableTh>
              <SortableTh field="categoryName" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Category & Type
              </SortableTh>
              <SortableTh field="flightNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Flight / Reg
              </SortableTh>
              <SortableTh field="severity" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Severity
              </SortableTh>
              <SortableTh field="isMandatory" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Type
              </SortableTh>
              <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Status
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((occ) => (
              <tr
                key={occ.id}
                onClick={() => onSelect(occ)}
                style={{ cursor: 'pointer' }}
              >
                <td style={{ fontWeight: 600, color: '#0284c7', fontFamily: 'monospace' }}>
                  <div>{occ.occurrenceNumber}</div>
                  {occ.correctiveActionNumber && (
                    <div style={{ marginTop: '3px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: '10px',
                        background: '#ecfdf5',
                        color: '#059669',
                        border: '1px solid #a7f3d0',
                        padding: '1px 5px',
                        borderRadius: '4px',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                      }}>
                        CAPA: {occ.correctiveActionNumber}
                      </span>
                    </div>
                  )}
                </td>
                <td style={{ maxWidth: '300px' }}>
                  <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '2px' }}>{occ.title}</div>
                  <div
                    style={{
                      color: '#64748b',
                      fontSize: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {occ.description}
                  </div>
                </td>
                <td style={{ color: '#475569', whiteSpace: 'nowrap', fontSize: '12px' }}>
                  {new Date(occ.eventDate).toLocaleDateString()} {new Date(occ.eventDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </td>
                <td>
                  <div style={{ color: '#1e293b', fontWeight: 500 }}>{occ.categoryName || 'General'}</div>
                  <div style={{ color: '#64748b', fontSize: '11px' }}>
                    {occ.typeName} {occ.icaoCode && `(${occ.icaoCode})`}
                  </div>
                </td>
                <td style={{ color: '#334155', fontSize: '12px' }}>
                  {occ.flightNumber || '-'} {occ.aircraftReg && `/ ${occ.aircraftReg}`}
                </td>
                <td>
                  {getSeverityBadge(occ.severity)}
                </td>
                <td>
                  {occ.isMandatory ? (
                    <span className="badge-pill badge-danger" title="Mandatory Occurrence Reporting (72h limit)">
                      MOR (72h)
                    </span>
                  ) : (
                    <span className="badge-pill badge-neutral">VOR</span>
                  )}
                </td>
                <td>
                  {getStatusBadge(occ.status)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(occ);
                    }}
                    className="btn-secondary"
                    style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="occurrences"
      />
    </div>
  );
};
